import {
  AT_HOME_SITUATION,
  GUARDIAN,
  HOME_CARE_SITUATION,
  PREDICAMENT_STATE_YES,
} from "core/consts";
import {
  AHBfloorSchema,
  AHBpostleitzahlSchema,
  createErrorMap,
  IKNumberSchema,
  predicamentSchema,
  siteIdSchema,
  unixDateSchema,
  ValidationMessagesMap,
} from "core/schemas/shared";
import { GKVGeneralFormData } from "core/types";
import Translations from "translations/types";
import { z } from "zod";
import { stripEmptyFields } from "./utils";

type GKVGeneralFormSchema = ReturnType<typeof createBaseGKVGeneralFormSchema>;

export const GUARDIAN_1 = 1 as const;
export const GUARDIAN_2 = 2 as const;

function createValidationMessages(
  translations: Translations,
): ValidationMessagesMap<GKVGeneralFormSchema> {
  const createTooLongMessage = (maxLength: number | bigint) =>
    translations.patientForms.characterLimitValidation({
      numberMost: maxLength.toString(),
    });

  const createTooShortMessage = (minLength: number | bigint) =>
    translations.patientForms.characterMinValidation({
      numberMinimum: minLength.toString(),
    });

  return {
    patient_first_name: {
      required: translations.patientForms.generalForm.firstNameRequired,
      tooLong: createTooLongMessage,
      tooShort: createTooShortMessage,
    },
    patient_last_name: {
      required: translations.patientForms.generalForm.lastNameRequired,
      tooLong: createTooLongMessage,
      tooShort: createTooShortMessage,
    },
    patient_birthdate: {
      required: translations.patientForms.generalForm.dateOfBirthRequired,
    },
    insurance_number: {
      required: translations.patientForms.generalForm.insuranceNumberRequired,
      tooLong: createTooLongMessage,
      tooShort: createTooShortMessage,
    },
    site_id_number: {
      required: translations.patientForms.generalForm.siteIdRequired,
      invalid: translations.patientForms.generalForm.siteIdRequired,
    },
    case_id: {
      required: translations.patientForms.generalForm.caseIdRequired,
      tooLong: createTooLongMessage,
    },
    living_lift_description: {
      required:
        translations.patientForms.generalForm.livingSituationFloorRequired,
      invalid:
        translations.patientForms.generalForm.livingSituationFloorRequired,
      tooLong: createTooLongMessage,
      tooShort: createTooShortMessage,
    },
    social_worker_last_name: {
      required: translations.patientForms.generalForm.lastNameRequired,
      tooLong: createTooLongMessage,
      tooShort: createTooShortMessage,
    },
    social_worker_phone_number: {
      required: translations.patientForms.generalForm.socialWorkerPhoneRequired,
      tooLong: createTooLongMessage,
      tooShort: createTooShortMessage,
    },
    default: {
      required: "field required",
      tooLong: createTooLongMessage,
      tooShort: createTooShortMessage,
      invalidUnion: translations.patientForms.generalForm.fieldRequired,
    },
  };
}

function createBaseGKVGeneralFormSchema(translations: Translations) {
  return z.object({
    // Section I
    insurance_ik_number: IKNumberSchema.nullish(),
    hospital_ik_number: IKNumberSchema.nullish(),

    patient_first_name: z.string().nonempty().max(45),
    patient_last_name: z.string().nonempty().max(45),
    patient_street: z.string().max(46).nullish(),
    patient_housenumber: z.string().max(9).nullish(),
    patient_zipcode: AHBpostleitzahlSchema.nullish(),
    patient_city: z.string().max(40).nullish(),
    patient_phone_number: z.string().max(25).nullish(),
    insurance_number: z.string().min(10).max(12),
    patient_birthdate: unixDateSchema,

    insured_details_application_insurance_benefits: predicamentSchema,
    retirement_pension_applied: predicamentSchema,
    retirement_pension_company: z.string().max(150).nullish(),
    retirement_pension_earning_capacity_applied: predicamentSchema,
    retirement_earning_capacity_company: z.string().max(150).nullish(),

    living_lift_description: AHBfloorSchema(translations),
    living_lift: predicamentSchema,
    living_special: z.string().max(600).nullish(),
    living_situation: z.nativeEnum(HOME_CARE_SITUATION, {
      errorMap: () => ({
        message: translations.patientForms.generalForm.fieldRequired,
      }),
    }),
    living_situation_at_home: z
      .nativeEnum(AT_HOME_SITUATION, {
        errorMap: () => ({
          message: translations.patientForms.generalForm.fieldRequired,
        }),
      })
      .nullish(),

    domestic_situation_is_safe: predicamentSchema,

    general_practitioner_first_name: z.string().max(45).nullish(),
    general_practitioner_last_name: z.string().max(45).nullish(),
    general_practitioner_street: z.string().max(46).nullish(),
    general_practitioner_housenumber: z.string().max(9).nullish(),
    general_practitioner_zipcode: AHBpostleitzahlSchema.nullish(),
    general_practitioner_city: z.string().max(40).nullish(),
    general_practitioner_phone_number: z.string().max(25).nullish(),

    insured_details_guardian_1_type: z.nativeEnum(GUARDIAN).nullish(),
    insured_details_guardian_1_first_name: z.string().max(45).nullish(),
    insured_details_guardian_1_last_name: z.string().max(45).nullish(),
    insured_details_guardian_1_street: z.string().max(46).nullish(),
    insured_details_guardian_1_house_number: z.string().max(9).nullish(),
    insured_details_guardian_1_zipcode: z.string().max(10).nullish(),
    insured_details_guardian_1_city: z.string().max(40).nullish(),
    insured_details_guardian_1_phone: z.string().max(25).nullish(),

    insured_details_guardian_2_type: z.nativeEnum(GUARDIAN).nullish(),
    insured_details_guardian_2_first_name: z.string().max(45).nullish(),
    insured_details_guardian_2_last_name: z.string().max(45).nullish(),
    insured_details_guardian_2_street: z.string().max(46).nullish(),
    insured_details_guardian_2_house_number: z.string().max(9).nullish(),
    insured_details_guardian_2_zipcode: z.string().max(10).nullish(),
    insured_details_guardian_2_city: z.string().max(40).nullish(),
    insured_details_guardian_2_phone: z.string().max(25).nullish(),

    reason_for_reha_clinic: z.string().max(600).nullish(),
    declaration_of_intent_confirmed: z.preprocess(
      (raw) => {
        // if it's exactly the string "1", coerce to number 1
        if (
          typeof raw === "string" &&
          raw.trim() === PREDICAMENT_STATE_YES.toString()
        ) {
          return PREDICAMENT_STATE_YES;
        }
        return raw;
      },
      z.literal(PREDICAMENT_STATE_YES, {
        errorMap: () => ({
          message: translations.patientForms.fieldRequiredValidation,
        }),
      }),
    ),
    data_transfer_consent_given: predicamentSchema,

    // Section II
    site_id_number: siteIdSchema,
    case_id: z.string().nonempty().max(15),

    social_worker_first_name: z.string().max(45).nullish(),
    social_worker_last_name: z.string().nonempty().max(45),
    social_worker_phone_number: z.string().nonempty().max(25),
    social_worker_fax_number: z.string().max(25).nullish(),

    communication_in_german_possible: predicamentSchema,
    communication_other_language: z.string().max(150).nullish(),

    prefered_clinic_contacted: predicamentSchema,
    prefered_clinic_name: z.string().max(45).nullish(),
    prefered_clinic_street: z.string().max(46).nullish(),
    prefered_clinic_house_number: z.string().max(9).nullish(),
    prefered_clinic_postal_code: z.string().max(10).nullish(),
    prefered_clinic_city: z.string().max(40).nullish(),
    prefered_clinic_phone: z.string().max(25).nullish(),
    prefered_clinic_contacted_start_date: unixDateSchema.nullish(),
  });
}

function applyGKVGeneralFormRefinements(
  schema: GKVGeneralFormSchema,
  translations: Translations,
) {
  return schema.superRefine((data: GKVGeneralFormData, ctx) => {
    const ensureGuardianCompleteness = (
      prefix: typeof GUARDIAN_1 | typeof GUARDIAN_2,
    ) => {
      // Guardian
      const type = data[`insured_details_guardian_${prefix}_type`];
      const first = data[`insured_details_guardian_${prefix}_first_name`];
      const last = data[`insured_details_guardian_${prefix}_last_name`];

      // Validate if any one of the 3 is present
      const hasAny = !!type || !!first || !!last;

      if (hasAny) {
        if (!type) {
          ctx.addIssue({
            path: [`insured_details_guardian_${prefix}_type`],
            code: z.ZodIssueCode.custom,
            message: `Vertretungstyp ist erforderlich, wenn Name angegeben ist`,
          });
        }
        if (!first) {
          ctx.addIssue({
            path: [`insured_details_guardian_${prefix}_first_name`],
            code: z.ZodIssueCode.custom,
            message: `Vorname ist erforderlich, wenn Vertretungstyp angegeben ist`,
          });
        }
        if (!last) {
          ctx.addIssue({
            path: [`insured_details_guardian_${prefix}_last_name`],
            code: z.ZodIssueCode.custom,
            message: `Nachname ist erforderlich, wenn Vertretungstyp angegeben ist`,
          });
        }
      }
    };

    ensureGuardianCompleteness(GUARDIAN_1);
    ensureGuardianCompleteness(GUARDIAN_2);

    // Living situation
    if (
      data.living_situation === HOME_CARE_SITUATION.HOME &&
      !data.living_situation_at_home
    ) {
      ctx.addIssue({
        path: ["living_situation_at_home"],
        code: z.ZodIssueCode.custom,
        message: translations.patientForms.generalForm.fieldRequired,
      });
    }

    // General Practitioner
    const gpFirst = data.general_practitioner_first_name?.trim();
    const gpLast = data.general_practitioner_last_name?.trim();
    const hasGP = !!gpFirst || !!gpLast;

    if (hasGP) {
      if (!gpLast) {
        ctx.addIssue({
          path: ["general_practitioner_last_name"],
          code: z.ZodIssueCode.custom,
          message: "Nachname ist erforderlich, wenn Hausarzt angegeben ist",
        });
      }
      if (!gpFirst) {
        ctx.addIssue({
          path: ["general_practitioner_first_name"],
          code: z.ZodIssueCode.custom,
          message: "Vorname ist erforderlich, wenn Hausarzt angegeben ist",
        });
      }
      if (!data.general_practitioner_city) {
        ctx.addIssue({
          path: ["general_practitioner_city"],
          code: z.ZodIssueCode.custom,
          message: "Ort ist erforderlich, wenn Hausarzt angegeben ist",
        });
      }
    }

    // Preferred clinic
    if (data.prefered_clinic_contacted === PREDICAMENT_STATE_YES) {
      const name = data.prefered_clinic_name?.trim();
      const street = data.prefered_clinic_street?.trim();
      const house = data.prefered_clinic_house_number?.trim();
      const postal = data.prefered_clinic_postal_code?.trim();
      const city = data.prefered_clinic_city?.trim();
      const phone = data.prefered_clinic_phone?.trim();
      const startDate = data.prefered_clinic_contacted_start_date;

      if (!name) {
        ctx.addIssue({
          path: ["prefered_clinic_name"],
          code: z.ZodIssueCode.custom,
          message: "Name der Klinik ist erforderlich, wenn Kontakt erfolgt ist",
        });
      }
      if (!street) {
        ctx.addIssue({
          path: ["prefered_clinic_street"],
          code: z.ZodIssueCode.custom,
          message: "Straße ist erforderlich, wenn Kontakt erfolgt ist",
        });
      }
      if (!house) {
        ctx.addIssue({
          path: ["prefered_clinic_house_number"],
          code: z.ZodIssueCode.custom,
          message: "Hausnummer ist erforderlich, wenn Kontakt erfolgt ist",
        });
      }
      if (!postal) {
        ctx.addIssue({
          path: ["prefered_clinic_postal_code"],
          code: z.ZodIssueCode.custom,
          message: "Postleitzahl ist erforderlich, wenn Kontakt erfolgt ist",
        });
      }
      if (!city) {
        ctx.addIssue({
          path: ["prefered_clinic_city"],
          code: z.ZodIssueCode.custom,
          message: "Ort ist erforderlich, wenn Kontakt erfolgt ist",
        });
      }
      if (!phone) {
        ctx.addIssue({
          path: ["prefered_clinic_phone"],
          code: z.ZodIssueCode.custom,
          message: "Telefonnummer ist erforderlich, wenn Kontakt erfolgt ist",
        });
      }
      if (!startDate) {
        ctx.addIssue({
          path: ["prefered_clinic_contacted_start_date"],
          code: z.ZodIssueCode.custom,
          message: "Startdatum ist erforderlich, wenn Kontakt erfolgt ist",
        });
      }
    }
  });
}

export function getGKVGeneralFormSchema({
  skipRequiredValidation,
  translations,
}: {
  skipRequiredValidation?: boolean;
  translations: Translations;
}) {
  const messages = createValidationMessages(translations);
  z.setErrorMap(createErrorMap<GKVGeneralFormSchema>(messages));

  const baseSchema = createBaseGKVGeneralFormSchema(translations);
  const partialSchema = baseSchema.partial();

  return skipRequiredValidation
    ? z.preprocess(stripEmptyFields, partialSchema)
    : applyGKVGeneralFormRefinements(baseSchema, translations);
}
